import React from 'react';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import type { EnhancedFieldConfiguration, Size } from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';
import { handleTruncationTooltip } from './truncationTooltip';

interface DotDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	showLabel?: boolean;
}

export function DotDisplay({
	field,
	value,
	data,
	showLabel = false,
}: DotDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig } = field;

	// Get display value and color using value mapping
	const { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);

	// Resolve theme-aware color
	const resolvedColor = colorValue?.text
		? resolveThemeColor(colorValue.text, isDark)
		: null;

	// Dot size mapping ('xxs'/'xl' fall back to md)
	const dotSizeMap: Partial<Record<Size, string>> = {
		xs: 'w-1.5 h-1.5',
		sm: 'w-2 h-2',
		md: 'w-2.5 h-2.5',
		lg: 'w-3 h-3',
	};

	const dotSizeClass =
		(styleConfig?.size != null ? dotSizeMap[styleConfig.size] : undefined) ??
		'w-2.5 h-2.5';

	// Build dot style - use text color for consistency
	const dotStyle: React.CSSProperties = {
		backgroundColor: resolvedColor ?? colors.values.textSecondary,
		// Add subtle border for better visibility
		boxShadow:
			resolvedColor != null
				? 'inset 0 0 0 1px rgba(0,0,0,0.1)'
				: `inset 0 0 0 1px ${colors.values.borderDefault}`,
	};

	return (
		<div className="flex items-center mr-1">
			<div className={cn(dotSizeClass, 'rounded-full')} style={dotStyle} />

			{showLabel && displayValue && (
				<span
					className="ml-1.5 truncate"
					onMouseEnter={handleTruncationTooltip}
				>
					{t(String(displayValue))}
				</span>
			)}
		</div>
	);
}
